import React, { useEffect, useState, useContext, useCallback } from 'react';
import {
  CCard,
  CCardBody,
  CCardHeader,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CButton,
  CSpinner,
  CBadge,
  CFormInput,
  CFormSelect,
  CRow,
  CCol,
  CAlert,
  CPagination,
  CPaginationItem
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilReload, cilFilter, cilTrash, cilChartLine } from '@coreui/icons';
import { AuthUserContext } from '../../session/context';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../../utils/EnvironmentSwitcher';
import { SkeletonTable } from '../../../components/SkeletonLoaders';
import { FixedSizeList as VirtualList } from 'react-window';
import { fetchAllFhirBundleEntries } from '../../../utils/fhirPagination';

// LOINC code to display name mapping
const LOINC_DISPLAY = {
  '8867-4': 'Heart Rate',
  '8310-5': 'Body Temperature',
  '59408-5': 'Oxygen Saturation',
  '2339-0': 'Blood Glucose',
  '29463-7': 'Body Weight',
  '55423-8': 'Steps Taken',
  '93832-4': 'Sleep Duration',
  '8480-6': 'Systolic BP',
  '8462-4': 'Diastolic BP',
  '78950-9': 'Calories',
  '77299-2': 'Water Intake',
};

// Category colors for badges
const CATEGORY_COLORS = {
  'Heart Rate': 'danger',
  'Body Temperature': 'warning',
  'Oxygen Saturation': 'info',
  'Blood Glucose': 'primary',
  'Body Weight': 'secondary',
  'Steps Taken': 'success',
  'Sleep Duration': 'dark',
  'Systolic BP': 'danger',
  'Diastolic BP': 'danger',
  'Calories': 'warning',
  'Water Intake': 'info',
  'default': 'light'
};

/**
 * ObservationsTable - Displays FHIR observations in a sortable, filterable table
 */
const ObservationsTable = () => {
  const { authUser } = useContext(AuthUserContext);
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Sorting
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Get authentication token
  const getAuthToken = useCallback(async () => {
    try {
      if (authUser?.stsTokenManager?.accessToken) {
        return authUser.stsTokenManager.accessToken;
      }
      // Fallback to stored token
      return localStorage.getItem('mieFToken');
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }, [authUser]);

  // Fetch observations from FHIR server
  const fetchObservations = useCallback(async () => {
    if (!authUser?.uid) {
      setError('Please sign in to view observations');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const fhirEndpoint = `${config.fhirUrl}/fhir`;
      
      // Build query URL with filters — paginate through all bundle pages (FHIR default is ~20/page).
      let queryUrl = `${fhirEndpoint}/Observation?identifier=https://fhir.mieros.cloud/fhir/ids|${authUser.uid}&_sort=-date&_count=100`;
      
      if (dateFrom) {
        queryUrl += `&date=ge${dateFrom}`;
      }
      if (dateTo) {
        queryUrl += `&date=le${dateTo}`;
      }

      const bundleEntries = await fetchAllFhirBundleEntries(queryUrl, {
        'Content-Type': 'application/fhir+json',
        'Authorization': `Bearer ${token}`,
      }, {
        httpClient: (url, opts) => axios.get(url, { headers: opts.headers }).then((res) => ({
          ok: res.status >= 200 && res.status < 300,
          status: res.status,
          json: async () => res.data,
        })),
      });

      if (bundleEntries.length) {
        const processedObservations = bundleEntries.map(entry => {
          const resource = entry.resource;
          
          // Get display name from LOINC code or text
          const loincCode = resource.code?.coding?.find(c => c.system === 'http://loinc.org')?.code;
          const displayName = LOINC_DISPLAY[loincCode] || 
                             resource.code?.coding?.[0]?.display || 
                             resource.code?.text || 
                             'Unknown';
          
          return {
            id: resource.id,
            type: displayName,
            loincCode: loincCode || 'N/A',
            value: resource.valueQuantity?.value ?? 'N/A',
            unit: resource.valueQuantity?.unit || '',
            date: resource.effectiveDateTime || resource.meta?.lastUpdated,
            status: resource.status,
            category: resource.category?.[0]?.coding?.[0]?.display || 'Observation'
          };
        });
        
        setObservations(processedObservations);
      } else {
        setObservations([]);
      }
    } catch (err) {
      console.error('Error fetching observations:', err);
      setError(err.message || 'Failed to fetch observations');
      toast.error('Failed to load observations');
    } finally {
      setLoading(false);
    }
  }, [authUser, getAuthToken, dateFrom, dateTo]);

  // Delete an observation
  const handleDelete = async (observationId) => {
    if (!window.confirm('Are you sure you want to delete this observation?')) {
      return;
    }

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      await axios.delete(`${config.fhirUrl}/fhir/Observation/${observationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      toast.success('Observation deleted');
      setObservations(prev => prev.filter(obs => obs.id !== observationId));
    } catch (err) {
      console.error('Error deleting observation:', err);
      toast.error('Failed to delete observation');
    }
  };

  // Initial load
  useEffect(() => {
    fetchObservations();
  }, [fetchObservations]);

  // Filter and sort observations
  const filteredObservations = observations
    .filter(obs => !typeFilter || obs.type.toLowerCase().includes(typeFilter.toLowerCase()))
    .sort((a, b) => {
      let comparison = 0;
      if (sortField === 'date') {
        comparison = new Date(a.date) - new Date(b.date);
      } else if (sortField === 'type') {
        comparison = a.type.localeCompare(b.type);
      } else if (sortField === 'value') {
        comparison = (a.value || 0) - (b.value || 0);
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  const useVirtualizedRows = filteredObservations.length > 100;

  // Pagination
  const totalPages = Math.ceil(filteredObservations.length / itemsPerPage);
  const paginatedObservations = filteredObservations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const displayedObservations = useVirtualizedRows ? filteredObservations : paginatedObservations;

  // Get unique observation types for filter dropdown
  const observationTypes = [...new Set(observations.map(obs => obs.type))].sort();

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get badge color for observation type
  const getBadgeColor = (type) => {
    return CATEGORY_COLORS[type] || CATEGORY_COLORS.default;
  };

  // Render sort direction indicator in a readable format
  const getSortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? '^' : 'v';
  };

  const virtualColumns = [
    { key: 'type', label: `Observation Type ${getSortIndicator('type')}`, width: '26%' },
    { key: 'value', label: `Measured Value ${getSortIndicator('value')}`, width: '18%' },
    { key: 'loinc', label: 'LOINC', width: '14%' },
    { key: 'date', label: `Observation Date ${getSortIndicator('date')}`, width: '22%' },
    { key: 'status', label: 'Status', width: '12%' },
    { key: 'actions', label: 'Actions', width: '8%' },
  ];

  const VirtualObservationRow = ({ index, style }) => {
    const obs = displayedObservations[index];
    if (!obs) return null;

    return (
      <div
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid var(--border-subtle)',  /* was: var(--cui-border-color, #dee2e6) — fallback was light grey */
          fontSize: '0.9rem',
          padding: '0 0.5rem',
          color: 'var(--text-primary)',
        }}
      >
        <div style={{ width: '26%', paddingRight: '0.5rem' }}>
          <CBadge color={getBadgeColor(obs.type)}>{obs.type}</CBadge>
        </div>
        <div style={{ width: '18%', paddingRight: '0.5rem' }}>
          <strong>{obs.value}</strong> {obs.unit}
        </div>
        <div style={{ width: '14%', paddingRight: '0.5rem' }}>
          <code>{obs.loincCode}</code>
        </div>
        <div style={{ width: '22%', paddingRight: '0.5rem' }}>{formatDate(obs.date)}</div>
        <div style={{ width: '12%', paddingRight: '0.5rem' }}>
          <CBadge color={obs.status === 'final' ? 'success' : 'warning'}>{obs.status}</CBadge>
        </div>
        <div style={{ width: '8%' }}>
          <CButton
            color="danger"
            size="sm"
            variant="ghost"
            onClick={() => handleDelete(obs.id)}
            title="Delete observation"
          >
            <CIcon icon={cilTrash} />
          </CButton>
        </div>
      </div>
    );
  };

  return (
    <CCard className="mb-4 observations-table-card">
      <CCardHeader className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <CIcon icon={cilChartLine} className="me-2" />
          <strong>FHIR Observations</strong>
          <CBadge color="primary" className="ms-2">{filteredObservations.length}</CBadge>
        </div>
        <CButton 
          color="primary" 
          size="sm" 
          onClick={fetchObservations}
          disabled={loading}
        >
          <CIcon icon={cilReload} className="me-1" />
          Refresh
        </CButton>
      </CCardHeader>
      
      <CCardBody>
        {/* Filters */}
        <CRow className="mb-3 g-3">
          <CCol md={4}>
            <CFormSelect
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
              aria-label="Filter by type"
            >
              <option value="">All Types</option>
              {observationTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </CFormSelect>
          </CCol>
          <CCol md={3}>
            <CFormInput
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              placeholder="From date"
            />
          </CCol>
          <CCol md={3}>
            <CFormInput
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              placeholder="To date"
            />
          </CCol>
          <CCol md={2}>
            <CButton 
              color="secondary" 
              variant="outline"
              onClick={() => { setTypeFilter(''); setDateFrom(''); setDateTo(''); }}
            >
              <CIcon icon={cilFilter} className="me-1" />
              Clear
            </CButton>
          </CCol>
        </CRow>

        {/* Error Alert */}
        {error && (
          <CAlert color="danger" className="mb-3">
            {error}
          </CAlert>
        )}

        {/* Loading State - use skeleton for initial load */}
        {loading ? (
          <SkeletonTable rows={5} columns={6} />
        ) : filteredObservations.length === 0 ? (
          <CAlert color="info">
            No observations found. Add some observations using the form above or load demo data from the Admin page.
          </CAlert>
        ) : (
          <>
            {/* Table */}
            {useVirtualizedRows ? (
              <div className="observations-table">
                <div
                  className="observations-table-head"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'var(--text-secondary)',
                    background: 'rgba(0, 212, 255, 0.06)',
                    borderBottom: '2px solid var(--border-default)',  /* was: var(--cui-border-color, #dee2e6) */
                    padding: '0.5rem',
                  }}
                >
                  {virtualColumns.map((col) => (
                    <div
                      key={col.key}
                      style={{
                        width: col.width,
                        cursor: ['type', 'value', 'date'].includes(col.key) ? 'pointer' : 'default',
                      }}
                      onClick={
                        col.key === 'type' || col.key === 'value' || col.key === 'date'
                          ? () => handleSort(col.key)
                          : undefined
                      }
                    >
                      {col.label}
                    </div>
                  ))}
                </div>
                <VirtualList
                  height={460}
                  itemCount={displayedObservations.length}
                  itemSize={58}
                  width="100%"
                >
                  {VirtualObservationRow}
                </VirtualList>
              </div>
            ) : (
              <CTable hover responsive striped className="observations-table">
                <CTableHead className="observations-table-head">
                  <CTableRow>
                    <CTableHeaderCell 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('type')}
                    >
                      Observation Type {getSortIndicator('type')}
                    </CTableHeaderCell>
                    <CTableHeaderCell 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('value')}
                    >
                      Measured Value {getSortIndicator('value')}
                    </CTableHeaderCell>
                    <CTableHeaderCell>LOINC</CTableHeaderCell>
                    <CTableHeaderCell 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('date')}
                    >
                      Observation Date {getSortIndicator('date')}
                    </CTableHeaderCell>
                    <CTableHeaderCell>Status</CTableHeaderCell>
                    <CTableHeaderCell>Actions</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {displayedObservations.map((obs) => (
                    <CTableRow key={obs.id}>
                      <CTableDataCell>
                        <CBadge color={getBadgeColor(obs.type)} className="me-2">
                          {obs.type}
                        </CBadge>
                      </CTableDataCell>
                      <CTableDataCell>
                        <strong>{obs.value}</strong> {obs.unit}
                      </CTableDataCell>
                      <CTableDataCell>
                        <code>{obs.loincCode}</code>
                      </CTableDataCell>
                      <CTableDataCell>
                        {formatDate(obs.date)}
                      </CTableDataCell>
                      <CTableDataCell>
                        <CBadge color={obs.status === 'final' ? 'success' : 'warning'}>
                          {obs.status}
                        </CBadge>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CButton 
                          color="danger" 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleDelete(obs.id)}
                          title="Delete observation"
                        >
                          <CIcon icon={cilTrash} />
                        </CButton>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            )}

            {/* Pagination */}
            {!useVirtualizedRows && totalPages > 1 && (
              <CPagination className="justify-content-center mt-3">
                <CPaginationItem 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >
                  First
                </CPaginationItem>
                <CPaginationItem 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                >
                  Previous
                </CPaginationItem>
                
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <CPaginationItem
                      key={pageNum}
                      active={currentPage === pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </CPaginationItem>
                  );
                })}
                
                <CPaginationItem 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  Next
                </CPaginationItem>
                <CPaginationItem 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  Last
                </CPaginationItem>
              </CPagination>
            )}

            {/* Summary */}
            <div className="mb-2 text-center mt-2 observations-table-summary">
              {useVirtualizedRows
                ? `Showing ${displayedObservations.length} virtualized observations`
                : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(currentPage * itemsPerPage, filteredObservations.length)} of ${filteredObservations.length} total observations`}
            </div>
          </>
        )}
      </CCardBody>
    </CCard>
  );
};

export default ObservationsTable;



